import React, { useEffect, useContext, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import mixpanel from 'mixpanel-browser';
import { HomeScreen } from './home/home';
import { SearchScreen } from './search/search';
import { CategoriesSearchScreen } from './categories/categories';
import { CategoriesResults } from './categories/categories-results';
import HandoutViewer from './handout/handout-viewer';
import HandoutPDF from './handout/pdf/handout-pdf';
import { SettingsScreen } from './settings/settings';
import { SettingsMultiUserScreen } from './settings/settings-multiuser';
import { SettingsDevScreen } from './settings/settings-dev';
import VerificationCodeInput from '../unauthenticated/onboard/verification-code-input';
import AuthorsAndReferences from './handout/authors-and-references';
import SurveyScreen from './survey/survey';
import FeedbackScreen from './feedback';
import HandoutLayout from './handout/handout-context-layout';
import StoreContext from '../../react-core-lib/components/app/store-context';
import { UnsavedChangesProvider } from './UnsavedChangesContext';
import { CustomiseModeProvider } from './handout/customise-mode/CustomiseModeContext';
import Handouts from './settings/handouts';
import Checkout from '../checkout/checkout';
import { SnackbarProvider } from './SnackbarProvider';
import Navigation from './Navigation';
import ManageSubscriptionWrapper from './settings/manage-subscription';
// import Models3DScreen from './models3D/models-3D-screen';
import MyLibrary from './handout/whiteboard/my-library';
import MyClinic from './handout/whiteboard/my-clinic';
import MyClinicWhiteboards from './handout/whiteboard/my-clinic-whiteboards';
import ArchivedHandouts from './handout/whiteboard/archive';
import { Modal, Box } from '@mui/material';
import PinkGradientButtonWithArrow from '../UIControls/PinkGradientButtonWithArrow';

import { AuthState } from '../../stores/auth-store';
import ConfirmationDialog from '../UIControls/ConfirmationDialog';
import ErrorDialog from '../UIControls/api-button/ErrorDialog';
import {
    notifyAuthExpired,
    isAuthExpiredDialogVisible,
    isAuthExpiredOverrideActive,
    setAuthExpiredDialogVisible,
    SESSION_REFRESH_MESSAGE,
    SESSION_REFRESH_TITLE,
    subscribeAuthExpired,
} from '../../utils/auth-error-utils';

const AuthenticatedApp = observer(() => {
    const { userDataStore, authStore } = useContext(StoreContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [showOnboardingModal, setShowOnboardingModal] = useState(false);
    const [pendingWhiteboardPromptOpen, setPendingWhiteboardPromptOpen] = useState(false);
    const [pendingWhiteboardData, setPendingWhiteboardData] = useState(null);
    const [pendingCustomisedPromptOpen, setPendingCustomisedPromptOpen] = useState(false);
    const [pendingCustomisedData, setPendingCustomisedData] = useState(null);
    const [pendingCombinedPromptOpen, setPendingCombinedPromptOpen] = useState(false);
    const [pendingCombinedData, setPendingCombinedData] = useState(null);
    const [authExpiredDialogOpen, setAuthExpiredDialogOpen] = useState(false);
    const PENDING_UPGRADE_HANDOUT_ID_KEY = 'pendingUpgradeHandoutId';
    const PENDING_UPGRADE_HANDOUT_ID_SET_AT_KEY = 'pendingUpgradeHandoutIdSetAt';
    const setPendingUpgradeHandoutId = (handoutId) => {
        if (!handoutId) return;
        sessionStorage.setItem(PENDING_UPGRADE_HANDOUT_ID_KEY, handoutId);
        sessionStorage.setItem(PENDING_UPGRADE_HANDOUT_ID_SET_AT_KEY, String(Date.now()));
    };

    useEffect(() => {
        if (authStore.authState === AuthState.IFRAME_AUTHENTICATED) return;

        const fetchUserAndTrackPageView = async () => {
            try {
                mixpanel.identify(userDataStore.userData.emailAddress);
                mixpanel.people.set({
                    $first_name: userDataStore.userData.firstName,
                    $last_name: userDataStore.userData.lastName,
                    $email: userDataStore.userData.emailAddress,
                    Mobile: userDataStore.userData.mobile,
                    'Clinic Name': userDataStore.userData.clinicName,
                    'Clinic Number': userDataStore.userData.clinicNumber,
                    'Clinic Size': userDataStore.userData.clinicSize,
                });
            } catch (error) {
                console.error('Error fetching current user for Mixpanel:', error);
            }
        };

        fetchUserAndTrackPageView();
    }, [location]);

    useEffect(() => {
        const handleShowModal = () => setShowOnboardingModal(true);
        window.addEventListener('trigger-onboarding-modal', handleShowModal);
        return () => window.removeEventListener('trigger-onboarding-modal', handleShowModal);
    }, []);

    useEffect(() => {
        const handleAuthExpired = () => {
            setAuthExpiredDialogOpen(true);
            setAuthExpiredDialogVisible(true);
        };

        const unsubscribe = subscribeAuthExpired(handleAuthExpired);

        if (isAuthExpiredDialogVisible()) {
            setAuthExpiredDialogOpen(true);
            setAuthExpiredDialogVisible(true);
        }

        if (isAuthExpiredOverrideActive()) {
            notifyAuthExpired({ source: 'dev-override', forced: true });
        }

        return unsubscribe;
    }, []);

    useEffect(() => {
        const isOnboardingComplete = localStorage.getItem('onboardingComplete') !== null;
        if (isOnboardingComplete) {
            setShowOnboardingModal(true);
            localStorage.removeItem('onboardingComplete');
        }
    }, []);

    useEffect(() => {
        const accountType = userDataStore?.subscriptionData?.accountType;
        if (!accountType) return;
        if (accountType === 'Free') return;
        if (location.pathname.startsWith('/handout/')) return;

        const combinedSave = localStorage.getItem('pendingCombinedHandoutSave');
        if (combinedSave) {
            try {
                const data = JSON.parse(combinedSave);
                const isRecent = !data.timestamp || Date.now() - data.timestamp < 3600000;
                if (!isRecent) {
                    localStorage.removeItem('pendingCombinedHandoutSave');
                    return;
                }
                if (!data.handoutId) return;
                const dismissedAt = Number(sessionStorage.getItem('pendingCombinedPromptDismissedAt') || 0);
                if (dismissedAt && dismissedAt >= data.timestamp) return;

                setPendingCombinedData(data);
                setPendingCombinedPromptOpen(true);
                return;
            } catch (error) {
                console.error('Error parsing pending combined save:', error);
                localStorage.removeItem('pendingCombinedHandoutSave');
                return;
            }
        }

        const pendingSave = localStorage.getItem('pendingWhiteboardSave');
        if (!pendingSave) return;

        try {
            const data = JSON.parse(pendingSave);
            const isRecent = !data.timestamp || Date.now() - data.timestamp < 3600000;
            if (!isRecent) {
                localStorage.removeItem('pendingWhiteboardSave');
                return;
            }
            if (!data.handoutId) return;
            const dismissedAt = Number(sessionStorage.getItem('pendingWhiteboardPromptDismissedAt') || 0);
            if (dismissedAt && dismissedAt >= data.timestamp) return;

            setPendingWhiteboardData(data);
            setPendingWhiteboardPromptOpen(true);
        } catch (error) {
            console.error('Error parsing pending whiteboard save:', error);
            localStorage.removeItem('pendingWhiteboardSave');
        }
    }, [userDataStore?.subscriptionData?.accountType, location.pathname]);

    useEffect(() => {
        const accountType = userDataStore?.subscriptionData?.accountType;
        if (!accountType) return;
        if (accountType === 'Free') return;
        if (location.pathname.startsWith('/handout/')) return;
        if (pendingWhiteboardPromptOpen || pendingCombinedPromptOpen) return;

        const pendingSave = localStorage.getItem('pendingCustomisedHandoutSave');
        if (!pendingSave) return;

        try {
            const data = JSON.parse(pendingSave);
            const isRecent = !data.timestamp || Date.now() - data.timestamp < 3600000;
            if (!isRecent) {
                localStorage.removeItem('pendingCustomisedHandoutSave');
                return;
            }
            if (!data.handoutId) return;
            const dismissedAt = Number(sessionStorage.getItem('pendingCustomisedPromptDismissedAt') || 0);
            if (dismissedAt && dismissedAt >= data.timestamp) return;

            setPendingCustomisedData(data);
            setPendingCustomisedPromptOpen(true);
        } catch (error) {
            console.error('Error parsing pending customised handout save:', error);
            localStorage.removeItem('pendingCustomisedHandoutSave');
        }
    }, [
        userDataStore?.subscriptionData?.accountType,
        location.pathname,
        pendingWhiteboardPromptOpen,
        pendingCombinedPromptOpen,
    ]);

    return (
        <SnackbarProvider>
            <UnsavedChangesProvider>
                <CustomiseModeProvider>
                    <main id="main-content" style={{ zoom: document.documentElement.style.zoom || 1 }}>
                        <Navigation />
                        <Routes>
                            <Route path="/" element={<HomeScreen />} />
                            <Route path="/search" element={<SearchScreen />} />
                            <Route path="/search/categories" element={<CategoriesSearchScreen />} />
                            <Route path="/search/diagrams" element={<SearchScreen />} />
                            <Route path="/search/3dmodels" element={<SearchScreen />} />
                            <Route path="/categories-results" element={<CategoriesResults />} />
                            <Route path="/settings" element={<SettingsScreen />} />
                            <Route path="/settings-multiuser" element={<SettingsMultiUserScreen />} />
                            <Route path="/settings/manage-subscription" element={<ManageSubscriptionWrapper />} />
                            <Route path="/settings/dev" element={<SettingsDevScreen />} />
                            <Route path="/survey" element={<SurveyScreen />} />
                            <Route path="/feedback" element={<FeedbackScreen />} />
                            <Route path="/handout/:id" element={<HandoutLayout />}>
                                <Route index element={<HandoutViewer />} />
                                <Route path="pdf" element={<HandoutPDF />} />
                                <Route path="authors-and-references" element={<AuthorsAndReferences />} />
                            </Route>
                            <Route path="/code-input" element={<VerificationCodeInput />} />
                            <Route path="/handouts" element={<Handouts />} />
                            <Route path="/checkout" element={<Checkout />} />
                            <Route path="/my-library" element={<MyLibrary />} />
                            <Route path="/my-clinic" element={<MyClinic />} />
                            <Route path="/my-clinic-whiteboard/:patientId" element={<MyClinicWhiteboards />} />
                            <Route path="/archive" element={<ArchivedHandouts />} />
                            {/* <Route path="/3d-models" element={<Models3DScreen />} /> */}
                        </Routes>
                    </main>

                    <Modal
                        open={showOnboardingModal}
                        onClose={() => {}}
                        aria-labelledby="onboarding-video-modal"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        disableEscapeKeyDown
                        disableBackdropClick
                        disableEnforceFocus
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                bgcolor: 'transparent',
                                boxShadow: 'none',
                                p: 0,
                                outline: 'none',
                                maxWidth: '90%',
                                maxHeight: '90%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {/* Simple cross (×) element */}
                            <Box
                                onClick={() => setShowOnboardingModal(false)}
                                sx={{
                                    position: 'absolute',
                                    right: -48,
                                    top: -64,
                                    color: '#fff',
                                    fontSize: '60px',
                                    fontWeight: 300,
                                    cursor: 'pointer',
                                    zIndex: 1,
                                    '&:hover': {
                                        opacity: 0.8,
                                    },
                                    '@media (max-width: 991px)': {
                                        right: -20,
                                        top: -68,
                                    },
                                }}
                            >
                                ×
                            </Box>

                            <Box
                                sx={{
                                    width: '852px',
                                    height: '570px',
                                    position: 'relative',
                                    paddingTop: '56.25%',
                                    borderTopLeftRadius: 8,
                                    borderTopRightRadius: 8,
                                    overflow: 'hidden',
                                    backgroundColor: '#000',
                                    '@media (max-width: 991px)': {
                                        width: '90vw',
                                        height: 'auto',
                                    },
                                }}
                            >
                                <iframe
                                    src="https://www.youtube.com/embed/LVvmAvysFFk?si=kMJdA2JRUTXo6jL7"
                                    title="Onboarding Video"
                                    frameBorder="0"
                                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        border: 0,
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    width: '100%',
                                    p: 2,
                                    display: 'flex',
                                    justifyContent: 'end',
                                    borderBottomLeftRadius: 8,
                                    borderBottomRightRadius: 8,
                                }}
                            >
                                <PinkGradientButtonWithArrow
                                    label="Skip Onboarding"
                                    onClick={() => setShowOnboardingModal(false)}
                                />
                            </Box>
                        </Box>
                    </Modal>

                    <ConfirmationDialog
                        open={pendingWhiteboardPromptOpen}
                        handleClose={() => {
                            setPendingWhiteboardPromptOpen(false);
                            if (pendingWhiteboardData?.timestamp) {
                                sessionStorage.setItem(
                                    'pendingWhiteboardPromptDismissedAt',
                                    String(pendingWhiteboardData.timestamp),
                                );
                            }
                        }}
                        onConfirm={() => {
                            if (pendingWhiteboardData?.handoutId) {
                                setPendingUpgradeHandoutId(pendingWhiteboardData.handoutId);
                                navigate(`/handout/${pendingWhiteboardData.handoutId}`);
                            }
                            setPendingWhiteboardPromptOpen(false);
                        }}
                        title="Unsaved whiteboard found"
                        message="Your whiteboard from before the upgrade is ready to be saved. Open the handout to finish saving it."
                        confirmText="Open handout"
                        cancelText="Later"
                    />
                    <ConfirmationDialog
                        open={pendingCombinedPromptOpen}
                        handleClose={() => {
                            setPendingCombinedPromptOpen(false);
                            if (pendingCombinedData?.timestamp) {
                                sessionStorage.setItem(
                                    'pendingCombinedPromptDismissedAt',
                                    String(pendingCombinedData.timestamp),
                                );
                            }
                        }}
                        onConfirm={() => {
                            if (pendingCombinedData?.handoutId) {
                                setPendingUpgradeHandoutId(pendingCombinedData.handoutId);
                                navigate(`/handout/${pendingCombinedData.handoutId}`);
                            }
                            setPendingCombinedPromptOpen(false);
                        }}
                        title="Unsaved handout & whiteboard found"
                        message="Your customised handout and whiteboard from before the upgrade are ready to be saved. Open the handout to finish saving them."
                        confirmText="Open handout"
                        cancelText="Later"
                    />
                    <ConfirmationDialog
                        open={pendingCustomisedPromptOpen}
                        handleClose={() => {
                            setPendingCustomisedPromptOpen(false);
                            if (pendingCustomisedData?.timestamp) {
                                sessionStorage.setItem(
                                    'pendingCustomisedPromptDismissedAt',
                                    String(pendingCustomisedData.timestamp),
                                );
                            }
                        }}
                        onConfirm={() => {
                            if (pendingCustomisedData?.handoutId) {
                                setPendingUpgradeHandoutId(pendingCustomisedData.handoutId);
                                sessionStorage.setItem(
                                    'forcePendingCustomisedHandoutId',
                                    pendingCustomisedData.handoutId,
                                );
                                navigate(`/handout/${pendingCustomisedData.handoutId}`);
                            }
                            setPendingCustomisedPromptOpen(false);
                        }}
                        title="Unsaved customised handout found"
                        message="Your customised handout from before the upgrade is ready to be saved. Open the handout to finish saving it."
                        confirmText="Open handout"
                        cancelText="Later"
                    />
                    <ErrorDialog
                        open={authExpiredDialogOpen}
                        handleClose={() => {
                            setAuthExpiredDialogOpen(false);
                            setAuthExpiredDialogVisible(false);
                        }}
                        title={SESSION_REFRESH_TITLE}
                        message={SESSION_REFRESH_MESSAGE}
                        primaryActionText="Reload Page"
                        onPrimaryAction={() => window.location.reload()}
                    />
                </CustomiseModeProvider>
            </UnsavedChangesProvider>
        </SnackbarProvider>
    );
});

export default AuthenticatedApp;
